/* rk14 - swipeable product gallery.
   Native scroll-snap track (touch + trackpad), pointer drag for mouse, and sync of
   counter / thumbnails / arrows / keyboard / variant image jumps.
   Capture-phase handlers on the section take over from rk4.js and rk9-conversion.js
   gallery handlers (those keep running for price, variants, etc.). */
(() => {
  if (window.__rk14Gallery) return;
  window.__rk14Gallery = true;

  const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
  const behavior = () => (reduceMotion.matches ? 'auto' : 'smooth');

  const init = section => {
    if (section.dataset.rk14Ready === 'true') return;
    const track = section.querySelector('[data-rk14-track]');
    if (!track) return;
    const slides = [...track.querySelectorAll('[data-rk4-slide]')];
    if (!slides.length) return;
    section.dataset.rk14Ready = 'true';

    const thumbs = [...section.querySelectorAll('[data-rk4-thumb]')];
    const thumbBox = section.querySelector('.rk4-pdp__thumbs');
    const counter = section.querySelector('[data-rk4-gallery-current]');
    const stage = section.querySelector('[data-rk4-stage]');
    const thumbFor = slide => thumbs.find(t => String(t.dataset.mediaId) === String(slide.dataset.mediaId));

    let current = slides.find(s => s.classList.contains('is-active')) || slides[0];
    let lockTarget = null;
    let lockTimer = 0;
    let raf = 0;

    const visibleSlides = () => slides.filter(s => !s.hidden);

    /* Variant filtering (rk9) hides thumbs; mirror that on slides, never hide the others. */
    const applyVisibility = () => {
      if (!thumbs.length) {
        slides.forEach(s => { if (s.hidden) s.hidden = false; });
        return;
      }
      slides.forEach(s => {
        const t = thumbFor(s);
        const show = !t || !t.hidden;
        if (s.hidden === show) s.hidden = !show;
      });
    };

    const releaseLock = () => {
      lockTarget = null;
      window.clearTimeout(lockTimer);
    };

    const setActive = slide => {
      if (!slide) return;
      current = slide;
      const list = visibleSlides();
      slides.forEach(s => {
        const on = s === slide;
        s.classList.toggle('is-active', on);
        if (!on) s.querySelector('video')?.pause?.();
      });
      thumbs.forEach(t => t.classList.toggle('is-active', t === thumbFor(slide)));
      if (counter) {
        counter.textContent = String(Math.max(1, list.indexOf(slide) + 1));
        if (counter.nextSibling?.nodeType === Node.TEXT_NODE) counter.nextSibling.nodeValue = ` / ${list.length}`;
      }
      const thumb = thumbFor(slide);
      if (thumb && thumbBox && !thumb.hidden) {
        const b = thumbBox.getBoundingClientRect();
        const r = thumb.getBoundingClientRect();
        const horizontal = thumbBox.scrollWidth > thumbBox.clientWidth + 1;
        const opts = { behavior: behavior() };
        if (horizontal) opts.left = thumbBox.scrollLeft + (r.left - b.left) - (b.width - r.width) / 2;
        else opts.top = thumbBox.scrollTop + (r.top - b.top) - (b.height - r.height) / 2;
        thumbBox.scrollTo(opts);
      }
    };

    const goTo = (slide, smooth = true) => {
      if (!slide || slide.hidden) return;
      const left = slide.offsetLeft;
      setActive(slide);
      if (Math.abs(track.scrollLeft - left) < 1) { releaseLock(); return; }
      if (smooth && !reduceMotion.matches) {
        lockTarget = slide;
        window.clearTimeout(lockTimer);
        lockTimer = window.setTimeout(releaseLock, 900);
        track.scrollTo({ left, behavior: 'smooth' });
      } else {
        releaseLock();
        track.scrollTo({ left, behavior: 'auto' });
      }
    };

    const step = direction => {
      const list = visibleSlides();
      if (!list.length) return;
      const index = Math.max(0, list.indexOf(current));
      goTo(list[(index + direction + list.length) % list.length]);
    };

    const slideAtScroll = () => {
      const list = visibleSlides();
      if (!list.length) return null;
      const w = track.clientWidth || 1;
      return list[Math.min(list.length - 1, Math.max(0, Math.round(track.scrollLeft / w)))];
    };

    track.addEventListener('scroll', () => {
      if (raf) return;
      raf = window.requestAnimationFrame(() => {
        raf = 0;
        if (lockTarget) {
          if (Math.abs(track.scrollLeft - lockTarget.offsetLeft) < 2) releaseLock();
          return;
        }
        const slide = slideAtScroll();
        if (slide && slide !== current) setActive(slide);
      });
    }, { passive: true });

    track.addEventListener('touchstart', releaseLock, { passive: true });

    /* Keep position when layout changes (variant filtering hides/shows slides). */
    const relayout = () => {
      applyVisibility();
      const list = visibleSlides();
      if (!list.length) return;
      if (current.hidden) current = list[0];
      releaseLock();
      track.scrollTo({ left: current.offsetLeft, behavior: 'auto' });
      setActive(current);
    };
    const observer = new MutationObserver(relayout);
    slides.forEach(s => observer.observe(s, { attributes: true, attributeFilter: ['hidden'] }));
    thumbs.forEach(t => observer.observe(t, { attributes: true, attributeFilter: ['hidden'] }));

    /* Take over arrows, thumbs and keyboard before rk4.js / rk9 handlers see them. */
    section.addEventListener('click', event => {
      const target = event.target;
      if (!(target instanceof Element)) return;
      const prev = target.closest('[data-rk4-gallery-prev]');
      const next = target.closest('[data-rk4-gallery-next]');
      const thumb = target.closest('[data-rk4-thumb]');
      if (!(prev || next || thumb) || !section.contains(target)) return;
      event.preventDefault();
      event.stopPropagation();
      if (prev) step(-1);
      else if (next) step(1);
      else goTo(slides.find(s => String(s.dataset.mediaId) === String(thumb.dataset.mediaId)) || slides[Number(thumb.dataset.rk4Thumb)]);
    }, true);

    section.addEventListener('keydown', event => {
      if (!stage || !stage.contains(event.target)) return;
      if (event.key !== 'ArrowLeft' && event.key !== 'ArrowRight') return;
      if (event.target.closest('video, iframe, input, select, textarea')) return;
      event.preventDefault();
      event.stopPropagation();
      step(event.key === 'ArrowLeft' ? -1 : 1);
    }, true);

    /* Variant change: rk4.js / rk9 mark the target slide with .is-active; scroll to it. */
    section.addEventListener('rocky:variant-change', () => {
      window.setTimeout(() => {
        applyVisibility();
        const target = slides.find(s => s.classList.contains('is-active') && !s.hidden) || current;
        if (target && !target.hidden) goTo(target);
      }, 0);
    });

    /* Native image drag must never start: it cancels the pointer stream (pointercancel) and steals the gesture. */
    const lockImages = () => track.querySelectorAll('img').forEach(img => { img.draggable = false; img.setAttribute('draggable', 'false'); });
    lockImages();
    track.addEventListener('dragstart', event => event.preventDefault(), true);

    /* Snap is switched off with inline styles for the whole drag (no dependence on media queries or classes),
       then restored once the settle animation is over. */
    let snapTimer = 0;
    const holdSnap = () => {
      window.clearTimeout(snapTimer);
      track.style.scrollSnapType = 'none';
      track.style.scrollBehavior = 'auto';
    };
    const restoreSnap = (delay = 520) => {
      window.clearTimeout(snapTimer);
      snapTimer = window.setTimeout(() => {
        track.style.scrollSnapType = '';
        track.style.scrollBehavior = '';
      }, delay);
    };
    const settleTo = slide => {
      if (!slide) return;
      holdSnap();
      goTo(slide);
      restoreSnap(reduceMotion.matches ? 60 : 560);
    };
    const indexNow = () => {
      const list = visibleSlides();
      const w = track.clientWidth || 1;
      return { list, index: Math.min(list.length - 1, Math.max(0, Math.round(track.scrollLeft / w))) };
    };

    /* Mouse drag (touch swipe is native scroll-snap). */
    if (slides.length > 1) track.classList.add('is-draggable');
    let drag = null;
    track.addEventListener('pointerdown', event => {
      if (event.pointerType !== 'mouse' || event.button !== 0 || slides.length < 2) return;
      if (event.target.closest('video, iframe, model-viewer, button, a, input, select, textarea, [controls]')) return;
      releaseLock();
      holdSnap();
      drag = { x: event.clientX, y: event.clientY, left: track.scrollLeft, moved: false, id: event.pointerId, samples: [{ x: event.clientX, t: performance.now() }] };
      stage?.focus?.({ preventScroll: true });
    });
    /* Block text selection / native drag that a mouse press would otherwise start. */
    track.addEventListener('mousedown', event => {
      if (event.button !== 0 || slides.length < 2) return;
      if (event.target.closest('video, iframe, model-viewer, button, a, input, select, textarea, [controls]')) return;
      event.preventDefault();
    });
    window.addEventListener('pointermove', event => {
      if (!drag || event.pointerId !== drag.id) return;
      const dx = event.clientX - drag.x;
      if (!drag.moved) {
        if (Math.abs(dx) < 4) return;
        drag.moved = true;
        track.classList.add('is-dragging');
        try { track.setPointerCapture(event.pointerId); } catch (e) { /* ignore */ }
      }
      const now = performance.now();
      drag.samples.push({ x: event.clientX, t: now });
      while (drag.samples.length > 2 && now - drag.samples[0].t > 100) drag.samples.shift();
      track.scrollLeft = drag.left - dx;
      event.preventDefault();
    });
    const endDrag = event => {
      if (!drag || (event && event.pointerId !== drag.id)) return;
      const state = drag;
      drag = null;
      if (!state.moved) { restoreSnap(0); return; }
      try { track.releasePointerCapture(state.id); } catch (e) { /* ignore */ }
      track.classList.remove('is-dragging');
      const first = state.samples[0];
      const last = state.samples[state.samples.length - 1];
      const dt = Math.max(1, last.t - first.t);
      const velocity = (last.x - first.x) / dt;
      const total = (event && event.type === 'pointerup' ? event.clientX : last.x) - state.x;
      const { list, index: nearest } = indexNow();
      const w = track.clientWidth || 1;
      const start = Math.min(list.length - 1, Math.max(0, Math.round(state.left / w)));
      let index = nearest;
      if (index === start && (Math.abs(total) > 40 || Math.abs(velocity) > 0.35)) {
        index = start + ((Math.abs(velocity) > 0.35 ? velocity : total) < 0 ? 1 : -1);
      }
      index = Math.min(list.length - 1, Math.max(0, index));
      const swallow = e => { e.preventDefault(); e.stopPropagation(); };
      track.addEventListener('click', swallow, { capture: true, once: true });
      window.setTimeout(() => track.removeEventListener('click', swallow, true), 50);
      settleTo(list[index]);
    };
    window.addEventListener('pointerup', endDrag);
    window.addEventListener('pointercancel', endDrag);
    track.addEventListener('lostpointercapture', event => { if (drag && drag.moved) endDrag(event); });

    /* Horizontal trackpad / tilt-wheel: one slide per gesture (momentum tail is ignored). */
    let wheelAcc = 0;
    let wheelLockUntil = 0;
    let wheelReset = 0;
    track.addEventListener('wheel', event => {
      if (slides.length < 2) return;
      if (Math.abs(event.deltaX) <= Math.abs(event.deltaY) || Math.abs(event.deltaX) < 1) return;
      event.preventDefault();
      releaseLock();
      const now = performance.now();
      if (now < wheelLockUntil) { wheelLockUntil = now + 140; return; }
      wheelAcc += event.deltaX;
      window.clearTimeout(wheelReset);
      wheelReset = window.setTimeout(() => { wheelAcc = 0; }, 160);
      if (Math.abs(wheelAcc) < 36) return;
      const { list, index } = indexNow();
      const next = Math.min(list.length - 1, Math.max(0, index + (wheelAcc > 0 ? 1 : -1)));
      wheelAcc = 0;
      wheelLockUntil = now + 500;
      settleTo(list[next]);
    }, { passive: false });

    /* Initial position (instant) once rk9's first sync has settled. */
    applyVisibility();
    track.scrollTo({ left: current.offsetLeft, behavior: 'auto' });
    setActive(current);
    window.setTimeout(() => {
      applyVisibility();
      const target = slides.find(s => s.classList.contains('is-active') && !s.hidden) || current;
      current = target;
      track.scrollTo({ left: target.offsetLeft, behavior: 'auto' });
      setActive(target);
    }, 50);
  };

  const initAll = root => (root || document).querySelectorAll('[data-rk4-product]').forEach(init);

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => initAll());
  else initAll();
  document.addEventListener('shopify:section:load', event => initAll(event.target));
})();
